import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, RotateCcw, XCircle } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LoadingOverlay } from "@/components/ui/loading-spinner";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  supportApi,
  SupportTicket,
  SupportTicketStatus,
  TICKET_CATEGORY_LABELS,
  formatPerson,
  getTicketAssignee,
  getTicketAssigneeValue,
} from "@/lib/api/support";
import { TicketStatusBadge } from "@/components/support/TicketStatusBadge";
import { TicketThread } from "@/components/support/TicketThread";
import { TicketComposer } from "@/components/support/TicketComposer";
import {
  TicketAssigneePicker,
  type AssigneeValue,
} from "@/components/support/TicketAssigneePicker";

interface TicketDetailPageProps {
  viewer: "patron" | "staff";
}

export default function TicketDetailPage({ viewer }: TicketDetailPageProps) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { hasPermission } = useAccessControl();
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [loading, setLoading] = useState(true);

  const currentActor = useMemo(
    () => ({ id: user?.id ?? "", isStaff: user?.isStaff ?? false }),
    [user?.id, user?.isStaff]
  );

  const fetchTicket = useCallback(() => {
    const loader = viewer === "staff" ? supportApi.getOne(id) : supportApi.getMine(id);
    return loader.then(setTicket).catch((err) => {
      toast.error(getApiErrorMessage(err, "Failed to load ticket"));
      navigate(viewer === "staff" ? "/admin/support" : "/support");
    });
  }, [id, viewer, navigate, toast]);

  useEffect(() => {
    setLoading(true);
    fetchTicket().finally(() => setLoading(false));
  }, [fetchTicket]);

  const handleReply = async (body: string) => {
    try {
      const updated =
        viewer === "staff"
          ? await supportApi.reply(id, body)
          : await supportApi.replyMine(id, body);
      setTicket(updated);
      await fetchTicket();
      toast.success("Reply sent");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to send reply"));
      throw err;
    }
  };

  const handleTransition = async (status: SupportTicketStatus) => {
    try {
      const updated = await supportApi.transition(id, status);
      setTicket(updated);
      await fetchTicket();
      toast.success(`Ticket ${status.toLowerCase().replace("_", " ")}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update ticket"));
    }
  };

  const handleAssign = async (next: AssigneeValue) => {
    try {
      const updated = await supportApi.assign(id, {
        assigneeKind: next?.kind ?? null,
        assigneeId: next?.id ?? null,
      });
      setTicket(updated);
      toast.success(next ? "Ticket assigned" : "Assignment cleared");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update assignment"));
    }
  };

  if (loading && !ticket) {
    return <LoadingOverlay />;
  }
  if (!ticket) return null;

  const isClosed = ticket.status === "CLOSED";
  const backPath = viewer === "staff" ? "/admin/support" : "/support";
  const backLabel = viewer === "staff" ? "Back to inbox" : "Back to my tickets";

  const canReplyStaff = hasPermission("support.reply");
  const canAssignTicket = hasPermission("support.assign");
  const canManageStatus = hasPermission("support.manage");
  const canReplyPatron = hasPermission("support.replyOwn") || hasPermission("support.createTicket");

  const assigneePerson = getTicketAssignee(ticket);

  return (
    <PageContainer>
      <Button variant="ghost" size="sm" onClick={() => navigate(backPath)} className="mb-2 gap-2">
        <ArrowLeft className="h-4 w-4" />
        {backLabel}
      </Button>

      <PageHeader
        title={ticket.subject}
        description={`${ticket.ticketNumber} · ${TICKET_CATEGORY_LABELS[ticket.category]}${
          ticket.databaseName ? ` · ${ticket.databaseName}` : ""
        }`}
        showSearch={false}
        titleSize="md"
        actions={<TicketStatusBadge status={ticket.status} patronView={viewer === "patron"} />}
      />

      {viewer === "staff" && (
        <Card className="mb-4 flex flex-wrap items-center gap-3 p-4">
          <div className="flex-1 text-sm text-slate-600">
            <div>
              <span className="font-medium text-slate-800">Patron:</span>{" "}
              {formatPerson(ticket.patron)}
            </div>
            <div className="mt-0.5 text-xs text-slate-500">
              Opened {new Date(ticket.createdAt).toLocaleString()}
            </div>
          </div>
          {canAssignTicket ? (
            <TicketAssigneePicker
              value={getTicketAssigneeValue(ticket)}
              onChange={handleAssign}
              disabled={isClosed}
            />
          ) : (
            <div className="min-w-[12rem] text-sm text-slate-600">
              <span className="font-medium text-slate-700">Assigned to:</span>{" "}
              {assigneePerson ? formatPerson(assigneePerson) : "Unassigned"}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {canManageStatus && ticket.status !== "RESOLVED" && !isClosed && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTransition("RESOLVED")}
                className="gap-2"
              >
                <CheckCircle2 className="h-4 w-4" />
                Mark resolved
              </Button>
            )}
            {canManageStatus &&
              (isClosed ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTransition("PENDING_STAFF")}
                  className="gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reopen
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTransition("CLOSED")}
                  className="gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  Close
                </Button>
              ))}
          </div>
        </Card>
      )}

      <Card className="p-4">
        <TicketThread
          messages={ticket.messages ?? []}
          currentActor={currentActor}
          patronUserId={ticket.patronUserId}
        />
        <div className="mt-4 border-t border-slate-100 pt-4">
          <TicketComposer
            disabled={viewer === "staff" ? isClosed || !canReplyStaff : isClosed || !canReplyPatron}
            disabledReason={
              isClosed
                ? viewer === "patron"
                  ? "This ticket is closed. Open a new one if you still need help."
                  : "This ticket is closed. Reopen it to reply."
                : viewer === "staff"
                  ? !canReplyStaff
                    ? "You do not have permission to reply to tickets."
                    : undefined
                  : !canReplyPatron
                    ? "You do not have permission to reply on this ticket."
                    : undefined
            }
            placeholder={viewer === "staff" ? "Reply to the patron…" : "Reply to the library team…"}
            submitLabel={viewer === "staff" ? "Send reply" : "Send"}
            onSubmit={handleReply}
          />
        </div>
      </Card>
    </PageContainer>
  );
}
